/**
 * NotesPanel — Rich notes popup for sticky notes
 * Features: view/edit mode, authors, comments, attachments, drag, resize, fullscreen
 */

import { useState, useRef, useEffect, useCallback } from 'react';

export default function NotesPanel({ notes, attachments, authors, comments, onNotesChange, onAttachmentsChange, onAddComment, onClose, currentUser }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(notes || '');
  const [commentText, setCommentText] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ w: 360, h: 480 });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const dragStart = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => { setEditText(notes || ''); }, [notes]);

  const commitEdit = () => {
    setEditing(false);
    if (editText !== notes) onNotesChange?.(editText);
  };

  const handleDragStart = (e) => {
    if (isFullscreen) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => setPosition({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
    const up = () => setDragging(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [dragging]);

  const handleResizeStart = (e) => {
    if (isFullscreen) return;
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    dragStart.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
  };

  useEffect(() => {
    if (!resizing) return;
    const move = (e) => setSize({
      w: Math.max(280, dragStart.current.w + (e.clientX - dragStart.current.x)),
      h: Math.max(300, dragStart.current.h + (e.clientY - dragStart.current.y)),
    });
    const up = () => setResizing(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [resizing]);

  const addAttachment = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      const newAttachments = [...(attachments || [])];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('projectId', 'brainstorm');
        try {
          const res = await fetch('/api/work/files/upload', { method: 'POST', credentials: 'include', body: formData });
          const d = await res.json();
          if (d.file) newAttachments.push({ id: d.file.id, name: d.file.name, size: d.file.size_bytes, type: d.file.mime_type });
        } catch {}
      }
      onAttachmentsChange?.(newAttachments);
    };
    input.click();
  };

  const addComment = () => {
    if (!commentText.trim()) return;
    onAddComment?.({
      text: commentText.trim(),
      author: currentUser || 'Anonymous',
      timestamp: new Date().toISOString(),
    });
    setCommentText('');
  };

  const panelStyle = isFullscreen ? {
    position: 'fixed', inset: '2rem', zIndex: 100, width: 'auto', height: 'auto',
  } : {
    position: 'absolute', left: `calc(100% + 8px)`, top: 0,
    transform: `translate(${position.x}px, ${position.y}px)`,
    width: size.w, height: size.h, zIndex: 50,
  };

  return (
    <div ref={panelRef} style={panelStyle} onClick={e => e.stopPropagation()}
      className="bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">

      {/* Header — draggable */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 cursor-move select-none flex-shrink-0"
        onMouseDown={handleDragStart}>
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          <h4 className="text-sm font-semibold text-gray-800">Notes</h4>
          {(authors || []).length > 0 && (
            <div className="flex -space-x-1 ml-2">
              {(authors || []).slice(0, 3).map((a, i) => (
                <div key={i} className="w-5 h-5 rounded-full bg-blue-500 text-white text-[8px] font-bold flex items-center justify-center border border-white" title={a}>
                  {(a || '?')[0].toUpperCase()}
                </div>
              ))}
              {(authors || []).length > 3 && <span className="text-[9px] text-gray-400 ml-1">+{authors.length - 3}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setIsFullscreen(!isFullscreen)} className="p-1 hover:bg-gray-100 rounded" title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            {isFullscreen ? (
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" /></svg>
            ) : (
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" /></svg>
            )}
          </button>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Notes content */}
        <div className="p-4">
          {editing ? (
            <textarea value={editText} onChange={e => setEditText(e.target.value)}
              onBlur={commitEdit} autoFocus
              className="w-full text-sm text-gray-700 border border-blue-300 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
              style={{ minHeight: isFullscreen ? 200 : 120 }}
              placeholder="Add detailed notes, context, research, URLs..." />
          ) : (
            <div onClick={() => setEditing(true)}
              className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap cursor-text min-h-[80px] hover:bg-gray-50 rounded-lg p-3 -m-3 transition-colors">
              {notes?.trim() ? (
                // Render with clickable links
                notes.split(/(\bhttps?:\/\/\S+)/g).map((part, i) =>
                  part.match(/^https?:\/\//) ? (
                    <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all"
                      onClick={e => e.stopPropagation()}>{part}</a>
                  ) : <span key={i}>{part}</span>
                )
              ) : (
                <span className="text-gray-400 italic">Click to add notes...</span>
              )}
            </div>
          )}
        </div>

        {/* Attachments */}
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold text-gray-400 uppercase">Attachments</span>
            <button onClick={addAttachment} className="text-[10px] text-blue-600 hover:text-blue-700 font-medium">+ Add file</button>
          </div>
          {(attachments || []).length > 0 ? (
            <div className="space-y-1">
              {attachments.map((att, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-2.5 py-2">
                  <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                  <span className="flex-1 truncate text-gray-700">{att.name}</span>
                  <span className="text-gray-400 flex-shrink-0">{att.size ? `${Math.round(att.size/1024)}KB` : ''}</span>
                  <button onClick={() => {
                    const newAtts = [...attachments];
                    newAtts.splice(i, 1);
                    onAttachmentsChange?.(newAtts);
                  }} className="text-gray-400 hover:text-red-500">&times;</button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-gray-400 text-center py-2">No attachments yet</div>
          )}
        </div>

        {/* Comments */}
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          <span className="text-[10px] font-semibold text-gray-400 uppercase mb-2 block">Comments</span>
          {(comments || []).length > 0 && (
            <div className="space-y-2 mb-3">
              {comments.map((c, i) => (
                <div key={i} className="bg-gray-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-4 h-4 rounded-full bg-blue-500 text-white text-[7px] font-bold flex items-center justify-center">
                      {(c.author || '?')[0].toUpperCase()}
                    </div>
                    <span className="text-[10px] font-medium text-gray-700">{c.author}</span>
                    <span className="text-[9px] text-gray-400">{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : ''}</span>
                  </div>
                  <p className="text-xs text-gray-600">{c.text}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input type="text" value={commentText} onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addComment(); }}
              className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300"
              placeholder="Add a comment..." />
            <button onClick={addComment} disabled={!commentText.trim()}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40">
              Post
            </button>
          </div>
        </div>
      </div>

      {/* Resize handle (bottom-right corner) */}
      {!isFullscreen && (
        <div onMouseDown={handleResizeStart}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize flex items-center justify-center">
          <svg className="w-3 h-3 text-gray-300" viewBox="0 0 24 24" fill="currentColor"><path d="M22 22H20V20H22V22ZM22 18H18V22H16V18H22V18ZM14 22H10V18H14V22Z"/></svg>
        </div>
      )}
    </div>
  );
}
